import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReportControls } from '../../../shared/types/appTypes';
import { GridFilterModel } from '@mui/x-data-grid';

// Helper function to get initial start date (today at 00:00:00)
const getInitialStartDate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

// Helper function to get initial end date (6 months from today at 23:59:59)
const getInitialEndDate = () => {
  const date = new Date();
  const sixMonthsFromNow = new Date(
    date.getFullYear(),
    date.getMonth() + 6,
    date.getDate()
  );
  sixMonthsFromNow.setHours(23, 59, 59, 999);
  return sixMonthsFromNow.toISOString();
};

// Get the initial dates
const initialStartDate = getInitialStartDate();
const initialEndDate = getInitialEndDate();

// Initial state for the reportControls slice
const initialState: ReportControls = {
  allEventsFilterModel: {
    items: [],
    quickFilterValues: []
  },
  gigsByDaySelectedDate: new Date().toISOString().split('T')[0], // Default to today
  // All Events Filter Controls
  allEventsNameSearch: '',
  allEventsCategoryFilter: 'events',
  allEventsStartDate: initialStartDate,
  allEventsEndDate: initialEndDate,
  allEventsCurrentSearchState: {
    startDate: null, // null indicates no search has been performed yet
    endDate: null,
    category: 'events',
    nameSearch: ''
  },
  allEventsHasPerformedInitialLoad: false
};

// Create the reportControls slice
const reportControlsSlice = createSlice({
  name: 'reportControls',
  initialState,
  reducers: {
    setAllEventsFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.allEventsFilterModel = action.payload;
    },
    setGigsByDaySelectedDate: (state, action: PayloadAction<string | null>) => {
      state.gigsByDaySelectedDate = action.payload;
    },
    setAllEventsNameSearch: (state, action: PayloadAction<string>) => {
      state.allEventsNameSearch = action.payload;
    },
    setAllEventsCategoryFilter: (state, action: PayloadAction<'events' | 'genavail'>) => {
      state.allEventsCategoryFilter = action.payload;
    },
    setAllEventsStartDate: (state, action: PayloadAction<string | null>) => {
      state.allEventsStartDate = action.payload;
    },
    setAllEventsEndDate: (state, action: PayloadAction<string | null>) => {
      state.allEventsEndDate = action.payload;
    },
    setAllEventsCurrentSearchState: (state, action: PayloadAction<{
      startDate: string | null;
      endDate: string | null;
      category: 'events' | 'genavail';
      nameSearch: string;
    }>) => {
      state.allEventsCurrentSearchState = action.payload;
    },
    setAllEventsHasPerformedInitialLoad: (state, action: PayloadAction<boolean>) => {
      state.allEventsHasPerformedInitialLoad = action.payload;
    },
    clearAllEventsFilters: (state) => {
      state.allEventsFilterModel = {
        items: [],
        quickFilterValues: []
      };
      state.allEventsNameSearch = '';
      state.allEventsCategoryFilter = 'events';
      state.allEventsStartDate = getInitialStartDate();
      state.allEventsEndDate = getInitialEndDate();
      state.allEventsCurrentSearchState = {
        startDate: null, // Reset to null to indicate no search performed
        endDate: null,
        category: 'events',
        nameSearch: ''
      };
    },
    resetReportControls: (state) => {
      return { ...initialState };
    }
  }
});

export const {
  setAllEventsFilterModel,
  setGigsByDaySelectedDate,
  setAllEventsNameSearch,
  setAllEventsCategoryFilter,
  setAllEventsStartDate,
  setAllEventsEndDate,
  setAllEventsCurrentSearchState,
  setAllEventsHasPerformedInitialLoad,
  clearAllEventsFilters,
  resetReportControls
} = reportControlsSlice.actions;

export default reportControlsSlice.reducer;
